import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import ImportLineRow from "../components/ImportLineRow";
import { ArrowLeft, FileArrowUp, CheckCircle, Warning, Sparkle, Truck } from "@phosphor-icons/react";

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-md text-sm";
const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);

export default function ImportChallan() {
  const nav = useNavigate();
  const { stores, user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [hdr, setHdr] = useState({ eway_bill_no: "", vehicle_no: "", transport_name: "", notes: "" });
  const [autoInward, setAutoInward] = useState(true);
  const [saveAliases, setSaveAliases] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const parse = async (f) => {
    if (!f) return;
    setFile(f); setParsing(true); setError(""); setPreview(null); setResult(null);
    const form = new FormData(); form.append("file", f);
    try {
      const token = localStorage.getItem("pos_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse/challans/import/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not read PDF");
      setPreview(data);
      setLines(data.lines.map((l, i) => ({ ...l, _id: i, product_name: l.resolved_name, master_sku: l.mode === "new" ? l.product_name : "", tally_name: l.product_name })));
      setStoreId(data.store?.store_id || "");
      setHdr({ eway_bill_no: data.header.eway_bill_no || "", vehicle_no: data.header.vehicle_no || "", transport_name: data.header.transport_name || "", notes: "" });
    } catch (e) {
      setError(e.message);
    } finally { setParsing(false); }
  };

  const onDrop = (e) => { e.preventDefault(); parse(e.dataTransfer.files?.[0]); };
  const updateLine = (id, patch) => setLines((ls) => ls.map((l) => (l._id === id ? { ...l, ...patch } : l)));
  const removeLine = (id) => setLines((ls) => ls.filter((l) => l._id !== id));
  const splitLine = (id) => setLines((ls) => {
    const out = [];
    let nextId = Math.max(...ls.map((l) => l._id)) + 1;
    for (const l of ls) {
      if (l._id !== id || l.candidates.length < 2) { out.push(l); continue; }
      const n = l.candidates.length;
      const base = Math.floor(l.qty / n);
      let rem = l.qty - base * n;
      l.candidates.forEach((c, i) => {
        const q = base + (rem > 0 ? 1 : 0); if (rem > 0) rem -= 1;
        out.push({
          ...l, _id: nextId++, qty: q, candidates: [c], status: c.matched ? "matched" : "new",
          mode: c.matched ? "existing" : "new", sku: c.matched ? c.sku : c.code, tally_code: c.code,
          product_name: c.matched ? c.name : l.tally_name, thumbnail: c.thumbnail || "",
          hsn: l.hsn || c.hsn || "", gst_rate: l.gst_rate || c.gst_rate || 0, _split: i + 1,
        });
      });
    }
    return out;
  });

  const store = useMemo(() => stores.find((s) => s.id === storeId), [stores, storeId]);
  const totals = useMemo(() => {
    const t = { units: 0, taxable: 0, tax: 0, newSkus: 0, review: 0 };
    for (const l of lines) {
      const taxable = (l.qty || 0) * (l.rate || 0);
      t.units += l.qty || 0; t.taxable += taxable; t.tax += taxable * (l.gst_rate || 0) / 100;
      if (l.mode === "new") t.newSkus += 1;
      if (!String(l.hsn || "").trim() || !(l.gst_rate > 0) || !String(l.sku || "").trim()) t.review += 1;
    }
    return t;
  }, [lines]);
  const blockers = lines.filter((l) => !String(l.hsn || "").trim() || !String(l.sku || "").trim());
  const canSubmit = !saving && preview && storeId && lines.length > 0 && blockers.length === 0;

  const commit = async () => {
    if (!canSubmit) return;
    if (totals.taxable + totals.tax > 50000 && !hdr.eway_bill_no.trim()) {
      if (!window.confirm(`Total ${inr(totals.taxable + totals.tax)} exceeds ₹50,000 and no E-Way Bill number is set. Create the Delivery Challan anyway?`)) return;
    }
    setSaving(true); setError("");
    try {
      const r = await api.post("/warehouse/challans/import/commit", {
        store_id: storeId,
        lines: lines.map((l) => ({
          mode: l.mode, sku: String(l.sku).trim(), product_name: l.product_name, tally_code: l.tally_code || "",
          hsn: String(l.hsn || "").trim(), gst_rate: parseFloat(l.gst_rate) || 0, qty: parseFloat(l.qty) || 0,
          rate: parseFloat(l.rate) || 0, master_sku: l.master_sku || "",
        })),
        ...hdr,
        source_doc_no: preview.header.doc_no, source_doc_date: preview.header.doc_date, source_file: preview.filename,
        auto_inward: autoInward, save_aliases: saveAliases,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  useEffect(() => { if (!storeId && !preview && stores.length) setStoreId(""); }, [stores, storeId, preview]);

  if (result) {
    const d = result.dispatch;
    return (
      <Shell>
        <PageHeader title="Delivery Challan Created" testId="import-done-header" />
        <div className="p-8 max-w-2xl">
          <div className="bg-white border border-emerald-200 rounded-md p-6" data-testid="import-result">
            <div className="flex items-center gap-3">
              <CheckCircle size={32} weight="fill" className="text-emerald-500" />
              <div>
                <div className="font-display font-bold text-xl">{d.invoice_no}</div>
                <div className="text-sm text-slate-600">{d.store_snapshot?.name} · {d.items.length} lines · {inr(d.grand_total)} · {d.status === "inwarded" ? "stock added to store" : "awaiting inward at store"}</div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold font-mono-num" data-testid="result-new-skus">{result.created_skus.length}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">New SKUs created</div></div>
              <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold font-mono-num">{result.aliases_saved}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">Tally codes mapped</div></div>
              <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold font-mono-num">{d.items.reduce((a, i) => a + i.qty, 0)}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">Units</div></div>
            </div>
            {result.created_skus.length > 0 && <div className="mt-4 text-xs text-slate-600"><b>New SKUs:</b> <span className="font-mono-num">{result.created_skus.join(", ")}</span></div>}
            <div className="mt-6 flex gap-2">
              <button onClick={() => nav("/warehouse/dispatches")} data-testid="goto-challans" className="h-10 px-5 bg-slate-900 text-white text-sm font-bold rounded-md">View Delivery Challans</button>
              <button onClick={() => { setResult(null); setPreview(null); setFile(null); setLines([]); }} className="h-10 px-4 border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-50">Upload another PDF</button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <PageHeader
        title="Upload Tally PDF → Delivery Challan"
        subtitle="Drop a Tally Tax Invoice or Delivery Challan PDF. Items are read automatically, matched to SKUs, and turned into a Delivery Challan."
        testId="import-challan-header"
        actions={<button onClick={() => nav("/warehouse/dispatches")} className="inline-flex items-center gap-1 h-10 px-4 border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-100"><ArrowLeft size={14} /> Back</button>}
      />
      <div className="p-8 space-y-5">
        {!preview && (
          <div
            onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="pdf-dropzone"
            className="border-2 border-dashed border-slate-300 hover:border-emerald-400 bg-white rounded-md p-14 text-center cursor-pointer transition-colors"
          >
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="pdf-file-input" onChange={(e) => parse(e.target.files?.[0])} />
            <FileArrowUp size={40} className="mx-auto text-emerald-500" weight="duotone" />
            <div className="mt-3 font-display font-bold text-lg">{parsing ? "Reading PDF…" : "Drop Tally PDF here or click to browse"}</div>
            <div className="text-xs text-slate-500 mt-1">Supports multi-page Tally “Tax Invoice” and “Delivery Challan” exports (text PDFs, up to 15 MB).</div>
            {file && <div className="mt-3 text-xs font-mono-num text-slate-600">{file.name}</div>}
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3" data-testid="import-error">{error}</div>}

        {preview && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="import-header-card">
                <div className="col-span-2 md:col-span-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Source document</div>
                    <div className="font-display font-bold text-lg font-mono-num" data-testid="src-doc-no">{preview.header.doc_no || "—"} <span className="text-sm text-slate-500 font-normal">dt. {preview.header.doc_date}</span></div>
                    <div className="text-xs text-slate-500">{preview.header.doc_type === "invoice" ? "Tally Tax Invoice" : "Tally Delivery Challan"} · {preview.pages} page(s) · consignee: {preview.header.consignee_name || "—"} {preview.header.consignee_gstin && <span className="font-mono-num">({preview.header.consignee_gstin})</span>}</div>
                  </div>
                  <button onClick={() => { setPreview(null); setFile(null); setLines([]); }} className="text-xs font-semibold text-slate-600 hover:text-slate-900 shrink-0">Change file</button>
                </div>
                <div className="col-span-2">
                  <Field label="Deliver to Store">
                    <select value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="import-store-select" className={`${inputCls} ${!storeId ? "border-amber-400" : ""}`}>
                      <option value="">— Select store —</option>
                      {stores.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.code} ({s.state_code})</option>)}
                    </select>
                  </Field>
                  <div className={`mt-1 text-[11px] ${preview.store?.store_id ? "text-emerald-700" : "text-amber-700"}`}>{preview.store?.reason}</div>
                </div>
                <Field label="E-Way Bill No."><input value={hdr.eway_bill_no} onChange={(e) => setHdr({ ...hdr, eway_bill_no: e.target.value })} data-testid="import-eway" className={`${inputCls} font-mono-num`} /></Field>
                <Field label="Vehicle No."><input value={hdr.vehicle_no} onChange={(e) => setHdr({ ...hdr, vehicle_no: e.target.value })} data-testid="import-vehicle" className={`${inputCls} font-mono-num`} /></Field>
                <Field label="Transporter"><input value={hdr.transport_name} onChange={(e) => setHdr({ ...hdr, transport_name: e.target.value })} className={inputCls} /></Field>
                <div className="col-span-2 md:col-span-3"><Field label="Notes"><input value={hdr.notes} onChange={(e) => setHdr({ ...hdr, notes: e.target.value })} className={inputCls} placeholder="Optional" /></Field></div>
              </div>

              <div className="bg-slate-900 text-white rounded-md p-5 space-y-3" data-testid="import-summary">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Import summary</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><div className="text-2xl font-bold font-mono-num" data-testid="sum-lines">{lines.length}</div><div className="text-[10px] text-slate-400 uppercase tracking-widest">Lines</div></div>
                  <div><div className="text-2xl font-bold font-mono-num" data-testid="sum-units">{totals.units}</div><div className="text-[10px] text-slate-400 uppercase tracking-widest">Units</div></div>
                  <div><div className="text-2xl font-bold font-mono-num text-emerald-400">{lines.length - totals.newSkus}</div><div className="text-[10px] text-slate-400 uppercase tracking-widest">Matched SKUs</div></div>
                  <div><div className="text-2xl font-bold font-mono-num text-amber-300" data-testid="sum-new">{totals.newSkus}</div><div className="text-[10px] text-slate-400 uppercase tracking-widest">New SKUs</div></div>
                </div>
                <div className="border-t border-slate-700 pt-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-400">Taxable</span><span className="font-mono-num">{inr(totals.taxable)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">GST {store && preview && store.state_code !== preview.header.seller_gstin?.slice(0, 2) ? "(IGST)" : "(CGST+SGST)"}</span><span className="font-mono-num">{inr(totals.tax)}</span></div>
                  <div className="flex justify-between font-bold text-base"><span>Total</span><span className="font-mono-num" data-testid="sum-total">{inr(totals.taxable + totals.tax)}</span></div>
                </div>
                {totals.taxable + totals.tax > 50000 && !hdr.eway_bill_no.trim() && (
                  <div className="text-[11px] text-amber-300 inline-flex items-start gap-1"><Warning size={14} weight="fill" className="shrink-0 mt-0.5" /> E-Way Bill required above ₹50,000</div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Items — review SKU matching ({lines.length})</div>
                <div className="flex items-center gap-4 text-[11px] text-slate-600">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Matched existing SKU</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> New SKU will be created</span>
                  {blockers.length > 0 && <span className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid="import-blockers"><Warning size={12} weight="fill" /> {blockers.length} line(s) need HSN / SKU code</span>}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="import-lines-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-10">#</th>
                      <th className="text-left px-3 py-2 font-semibold">From PDF</th>
                      <th className="text-left px-3 py-2 font-semibold min-w-[320px]">Maps to SKU</th>
                      <th className="text-left px-3 py-2 font-semibold">HSN</th>
                      <th className="text-right px-3 py-2 font-semibold">GST%</th>
                      <th className="text-right px-3 py-2 font-semibold">Qty</th>
                      <th className="text-right px-3 py-2 font-semibold">Rate</th>
                      <th className="text-right px-3 py-2 font-semibold">Taxable</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <ImportLineRow key={l._id} line={l} onChange={(p) => updateLine(l._id, p)} onRemove={() => removeLine(l._id)} onSplit={() => splitLine(l._id)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoInward} onChange={(e) => setAutoInward(e.target.checked)} data-testid="auto-inward-toggle" className="mt-0.5" />
                  <span><b className="inline-flex items-center gap-1"><Truck size={14} /> Add stock to store now</b> <span className="text-slate-500">— auto-inward: quantities are added to {store?.name || "the store"} inventory immediately. Untick to let store staff inward it on receipt.</span></span>
                </label>
                {preview.can_save_aliases && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={saveAliases} onChange={(e) => setSaveAliases(e.target.checked)} data-testid="save-aliases-toggle" className="mt-0.5" />
                    <span><b className="inline-flex items-center gap-1"><Sparkle size={14} /> Remember Tally code → SKU mappings</b> <span className="text-slate-500">— future PDFs with the same Tally codes will auto-match (admin only).</span></span>
                  </label>
                )}
              </div>
              <button onClick={commit} disabled={!canSubmit} data-testid="commit-import-btn" className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md text-sm disabled:opacity-50 shrink-0">
                {saving ? "Creating…" : `Create Delivery Challan${totals.newSkus ? ` + ${totals.newSkus} SKU${totals.newSkus > 1 ? "s" : ""}` : ""}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
